import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { ASSETS, FIRM_INFO } from '@/lib/assets';
import { X } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/', key: 'home', end: true },
  { to: '/cabinet', key: 'cabinet' },
  { to: '/competences', key: 'competences' },
  { to: '/formations', key: 'formations' },
  { to: '/equipe', key: 'equipe' },
  { to: '/valeurs', key: 'valeurs' },
  { to: '/honoraires', key: 'honoraires' },
  { to: '/contact', key: 'contact' },
];

export default function Sidebar({ onNavigate, isMobileOpen, onCloseMobile }) {
  const { t, lang, setLang } = useLang();

  const handleClick = () => {
    if (onNavigate) onNavigate();
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-screen z-40 flex flex-col bg-taupe text-cream w-[270px] shadow-2xl transition-transform duration-300 ease-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      data-testid="sidebar"
    >
      {/* Mobile close */}
      <button
        onClick={onCloseMobile}
        className="lg:hidden absolute top-4 right-4 text-cream/80 hover:text-gold transition"
        aria-label="Close menu"
      >
        <X size={22} />
      </button>

      {/* Logo */}
      <Link to="/" onClick={handleClick} className="block px-6 pt-10 pb-6" data-testid="sidebar-logo">
        <img
          src={ASSETS.logo}
          alt="VALTHELIS Avocats"
          className="w-full max-w-[180px] mx-auto select-none drop-shadow-md"
          draggable="false"
        />
      </Link>

      {/* Gold separator */}
      <div className="mx-6 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)] to-transparent opacity-70" />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-6" data-testid="sidebar-nav">
        <ul className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={handleClick}
                className={({ isActive }) =>
                  `group relative flex items-center px-5 py-3 text-[12.5px] tracking-[0.18em] uppercase font-medium transition-all duration-200
                  ${
                    isActive
                      ? 'text-[var(--color-gold-light)]'
                      : 'text-cream/85 hover:text-[var(--color-gold-light)]'
                  }`
                }
                data-testid={`nav-${item.key}`}
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] bg-[var(--color-gold)] transition-all duration-300 ${
                        isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 group-hover:opacity-60 group-hover:scale-y-75'
                      }`}
                    />
                    <span>{t.nav[item.key]}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom: language toggle + footer */}
      <div className="px-6 py-5 border-t border-[var(--color-gold)]/30">
        <div className="flex items-center justify-center gap-1 mb-4" data-testid="language-toggle">
          <button
            type="button"
            onClick={() => setLang('fr')}
            className={`px-3 py-1 text-[11px] tracking-[0.25em] font-semibold transition-colors ${
              lang === 'fr'
                ? 'text-[var(--color-gold)]'
                : 'text-cream/60 hover:text-cream'
            }`}
            aria-pressed={lang === 'fr'}
            data-testid="lang-fr"
          >
            FR
          </button>
          <span className="text-cream/40">|</span>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={`px-3 py-1 text-[11px] tracking-[0.25em] font-semibold transition-colors ${
              lang === 'en'
                ? 'text-[var(--color-gold)]'
                : 'text-cream/60 hover:text-cream'
            }`}
            aria-pressed={lang === 'en'}
            data-testid="lang-en"
          >
            EN
          </button>
        </div>
        <p className="text-[10px] tracking-[0.2em] text-cream/55 text-center uppercase font-light">
          © {new Date().getFullYear()} {FIRM_INFO.shortName}
        </p>
      </div>
    </aside>
  );
}
